import { useState } from "react";
import { Config } from "../config/Config";
import { IArticle } from "../models/IArticle";
import { ITestimonial, IPricingTier, IPartner, ITeamMember, ICaseStudy, ISiteSetting } from "../models/ICmsModels";

export interface ICmsUser {
	id: number;
	email: string;
}

export interface ICmsAdminApi {
	isLoading: boolean;
	// Auth
	validate: (token: string) => Promise<{ email: string; authorized: boolean } | null>;
	// Articles
	createArticle: (token: string, article: IArticle) => Promise<IArticle | null>;
	updateArticle: (token: string, article: IArticle) => Promise<IArticle | null>;
	deleteArticle: (token: string, id: number) => Promise<boolean>;
	// Testimonials
	createTestimonial: (token: string, item: ITestimonial) => Promise<ITestimonial | null>;
	updateTestimonial: (token: string, item: ITestimonial) => Promise<ITestimonial | null>;
	deleteTestimonial: (token: string, id: number) => Promise<boolean>;
	// Team Members
	createTeamMember: (token: string, item: ITeamMember) => Promise<ITeamMember | null>;
	updateTeamMember: (token: string, item: ITeamMember) => Promise<ITeamMember | null>;
	deleteTeamMember: (token: string, id: number) => Promise<boolean>;
	// Partners
	createPartner: (token: string, item: IPartner) => Promise<IPartner | null>;
	updatePartner: (token: string, item: IPartner) => Promise<IPartner | null>;
	deletePartner: (token: string, id: number) => Promise<boolean>;
	// Pricing Tiers
	createPricingTier: (token: string, item: IPricingTier) => Promise<IPricingTier | null>;
	updatePricingTier: (token: string, item: IPricingTier) => Promise<IPricingTier | null>;
	deletePricingTier: (token: string, id: number) => Promise<boolean>;
	// Case Studies
	createCaseStudy: (token: string, item: ICaseStudy) => Promise<ICaseStudy | null>;
	updateCaseStudy: (token: string, item: ICaseStudy) => Promise<ICaseStudy | null>;
	deleteCaseStudy: (token: string, id: number) => Promise<boolean>;
	// Site Settings
	upsertSiteSetting: (token: string, setting: ISiteSetting) => Promise<ISiteSetting | null>;
	deleteSiteSetting: (token: string, key: string) => Promise<boolean>;
	// Upload
	uploadImage: <T = unknown>(token: string, endpoint: string, file: File) => Promise<T | null>;
	// Users
	getUsers: (token: string) => Promise<ICmsUser[]>;
	addUser: (token: string, email: string) => Promise<ICmsUser | null>;
	deleteUser: (token: string, id: number) => Promise<boolean>;
}


export const useCmsAdminApi = (): ICmsAdminApi => {
	const baseUrl = Config.baseUrl;
	const [loadingCount, setLoadingCount] = useState(0);
	const isLoading = loadingCount > 0;

	const authHeaders = (token: string) => ({
		accept: "application/json",
		"Content-Type": "application/json",
		Authorization: `Bearer ${token}`,
	});

	const postJson = async <T,>(token: string, url: string, body: any): Promise<T | null> => {
		setLoadingCount(c => c + 1);
		try {
			const response = await fetch(url, { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) });
			if (response.status === 401) {
				sessionStorage.removeItem("cmsToken");
				window.location.href = "/cms/login";
				throw new Error("Unauthorized");
			}
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			return await response.json() as T;
		} catch (error) { console.error('CMS Admin API error:', error); throw error; } finally { setLoadingCount(c => c - 1); }
	};

	const putJson = async <T,>(token: string, url: string, body: any): Promise<T | null> => {
		setLoadingCount(c => c + 1);
		try {
			const response = await fetch(url, { method: "PUT", headers: authHeaders(token), body: JSON.stringify(body) });
			if (response.status === 401) {
				sessionStorage.removeItem("cmsToken");
				window.location.href = "/cms/login";
				throw new Error("Unauthorized");
			}
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			return await response.json() as T;
		} catch (error) { console.error('CMS Admin API error:', error); throw error; } finally { setLoadingCount(c => c - 1); }
	};

	const deleteReq = async (token: string, url: string): Promise<boolean> => {
		setLoadingCount(c => c + 1);
		try {
			const response = await fetch(url, { method: "DELETE", headers: authHeaders(token) });
			if (response.status === 401) {
				sessionStorage.removeItem("cmsToken");
				window.location.href = "/cms/login";
				throw new Error("Unauthorized");
			}
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			return true;
		} catch (error) { console.error('CMS Admin API error:', error); throw error; } finally { setLoadingCount(c => c - 1); }
	};

	const validate = async (token: string) => {
		setLoadingCount(c => c + 1);
		try {
			const url = `${baseUrl}api/v1/cmsauth/validate`;
			const response = await fetch(url, { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${token}` } });
			if (!response.ok) return null;
			return await response.json() as { email: string; authorized: boolean };
		} catch { return null; } finally { setLoadingCount(c => c - 1); }
	};

	const getJson = async <T,>(token: string, url: string, fallback: T): Promise<T> => {
		setLoadingCount(c => c + 1);
		try {
			const response = await fetch(url, { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${token}` } });
			if (response.status === 401) {
				sessionStorage.removeItem("cmsToken");
				window.location.href = "/cms/login";
				throw new Error("Unauthorized");
			}
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			return await response.json() as T;
		} catch (error) { console.error('CMS Admin API error:', error); throw error; } finally { setLoadingCount(c => c - 1); }
	};

	const uploadImage = async <T = unknown,>(token: string, endpoint: string, file: File): Promise<T | null> => {
		setLoadingCount(c => c + 1);
		try {
			const fd = new FormData();
			fd.append("file", file);
			const response = await fetch(`${baseUrl}${endpoint}`, {
				method: "POST",
				headers: { Authorization: `Bearer ${token}` },
				body: fd,
			});
			if (response.status === 401) {
				sessionStorage.removeItem("cmsToken");
				window.location.href = "/cms/login";
				throw new Error("Unauthorized");
			}
			if (!response.ok) return null;
			return await response.json() as T;
		} catch (error) { console.error('CMS Admin API error:', error); return null; } finally { setLoadingCount(c => c - 1); }
	};

	return {
		isLoading,
		validate,
		createArticle: (token, item) => postJson<IArticle>(token, `${baseUrl}api/v1/article`, item),
		updateArticle: (token, item) => putJson<IArticle>(token, `${baseUrl}api/v1/article`, item),
		deleteArticle: (token, id) => deleteReq(token, `${baseUrl}api/v1/article/${id}`),
		createTestimonial: (token, item) => postJson<ITestimonial>(token, `${baseUrl}api/v1/testimonial`, item),
		updateTestimonial: (token, item) => putJson<ITestimonial>(token, `${baseUrl}api/v1/testimonial`, item),
		deleteTestimonial: (token, id) => deleteReq(token, `${baseUrl}api/v1/testimonial/${id}`),
		createTeamMember: (token, item) => postJson<ITeamMember>(token, `${baseUrl}api/v1/teammember`, item),
		updateTeamMember: (token, item) => putJson<ITeamMember>(token, `${baseUrl}api/v1/teammember`, item),
		deleteTeamMember: (token, id) => deleteReq(token, `${baseUrl}api/v1/teammember/${id}`),
		createPartner: (token, item) => postJson<IPartner>(token, `${baseUrl}api/v1/partner`, item),
		updatePartner: (token, item) => putJson<IPartner>(token, `${baseUrl}api/v1/partner`, item),
		deletePartner: (token, id) => deleteReq(token, `${baseUrl}api/v1/partner/${id}`),
		createPricingTier: (token, item) => postJson<IPricingTier>(token, `${baseUrl}api/v1/pricingtier`, item),
		updatePricingTier: (token, item) => putJson<IPricingTier>(token, `${baseUrl}api/v1/pricingtier`, item),
		deletePricingTier: (token, id) => deleteReq(token, `${baseUrl}api/v1/pricingtier/${id}`),
		createCaseStudy: (token, item) => postJson<ICaseStudy>(token, `${baseUrl}api/v1/casestudy`, item),
		updateCaseStudy: (token, item) => putJson<ICaseStudy>(token, `${baseUrl}api/v1/casestudy`, item),
		deleteCaseStudy: (token, id) => deleteReq(token, `${baseUrl}api/v1/casestudy/${id}`),
		upsertSiteSetting: (token, setting) => postJson<ISiteSetting>(token, `${baseUrl}api/v1/sitesettings`, setting),
		deleteSiteSetting: (token, key) => deleteReq(token, `${baseUrl}api/v1/sitesettings/${encodeURIComponent(key)}`),
		uploadImage,
		getUsers: (token) => getJson<ICmsUser[]>(token, `${baseUrl}api/v1/cmsauth/users`, []),
		addUser: (token, email) => postJson<ICmsUser>(token, `${baseUrl}api/v1/cmsauth/users`, { email }),
		deleteUser: (token, id) => deleteReq(token, `${baseUrl}api/v1/cmsauth/users/${id}`),
	};
};
